/** Vite dev / preview ports where the SPA is served without the API on the same origin. */
const VITE_DEV_PORTS = new Set(['3000', '5173', '5174', '4173'])

function isRemoteGunslopApiUrl (url: string): boolean {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`)
    return u.hostname.endsWith('gunslop.com')
  } catch {
    return /gunslop\.com/i.test(url)
  }
}

/** Production site hosts that share one cookie/site policy; www vs apex must not cross-load media (Chrome ORB). */
function isGunslopApexOrWww (hostname: string): boolean {
  const h = hostname.toLowerCase()
  return h === 'gunslop.com' || h === 'www.gunslop.com'
}

/**
 * API base URL (e.g. http://localhost:5081/api). Production bundles bake VITE_API_URL from
 * .env.production; opening that build on localhost would otherwise call gunslop.com and hit CORS.
 * Prefer same-origin /api when the page is on loopback and not on a pure Vite port, or set
 * VITE_LOCAL_API_URL (e.g. http://localhost:5000/api when using vite preview on 4173).
 */
export function resolveApiBaseUrl (): string {
  const envUrl = import.meta.env.VITE_API_URL?.trim()
  const fallback = 'http://localhost:5000/api'
  const localExplicit = import.meta.env.VITE_LOCAL_API_URL?.trim()

  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase()
    const isLoopback = host === 'localhost' || host === '127.0.0.1' || host === '[::1]'

    // Baked VITE_API_URL uses apex; users on www must not call gunslop.com (CORS / ORB).
    if (isGunslopApexOrWww(host)) {
      return `${window.location.origin}/api`
    }

    if (isLoopback) {
      if (localExplicit) return localExplicit.replace(/\/$/, '')
      if (envUrl && isRemoteGunslopApiUrl(envUrl)) {
        const port = window.location.port
        if (port && VITE_DEV_PORTS.has(port)) return fallback
        return `${window.location.origin}/api`
      }
    }
  }

  return envUrl || fallback
}

/**
 * Origin (and optional path prefix) for static files served from the API host: `/videos`, `/thumbnails`,
 * `/shoots`, etc. These are not under `/api`.
 */
export function resolvePublicSiteBaseUrl (): string {
  let raw = resolveApiBaseUrl().trim().replace(/\/+$/, '')
  if (!/^https?:\/\//i.test(raw)) {
    raw = `http://${raw}`
  }
  let out: string
  try {
    const u = new URL(raw)
    let pathname = (u.pathname || '').replace(/\/+$/, '') || ''
    if (pathname.toLowerCase().endsWith('/api')) {
      pathname = pathname.slice(0, -4)
    }
    out = `${u.origin}${pathname}`.replace(/\/+$/, '') || u.origin
  } catch {
    if (raw.toLowerCase().endsWith('/api')) {
      out = raw.slice(0, -4).replace(/\/+$/, '')
    } else {
      out = raw
    }
  }

  // VITE_API_URL often uses apex while users open www (or reverse). Media would load cross-origin → ORB.
  if (typeof window !== 'undefined') {
    const pageHost = window.location.hostname.toLowerCase()
    if (isGunslopApexOrWww(pageHost)) {
      try {
        const bu = new URL(out.startsWith('http') ? out : `https://${out}`)
        if (isGunslopApexOrWww(bu.hostname)) {
          return window.location.origin
        }
      } catch {
        /* keep out */
      }
    }
  }

  return out
}

/** Rewrite apex ↔ www so <video>/<img> hits the same origin as the SPA (avoids blocked_by_orb). */
function alignGunslopMediaToPageOrigin (urlString: string): string {
  if (typeof window === 'undefined') return urlString
  try {
    const page = new URL(window.location.href)
    if (!isGunslopApexOrWww(page.hostname)) return urlString

    const resolved = urlString.startsWith('//')
      ? new URL(`${page.protocol}${urlString}`)
      : new URL(urlString)

    if (!isGunslopApexOrWww(resolved.hostname)) return urlString
    if (resolved.origin === page.origin) return urlString

    return `${page.origin}${resolved.pathname}${resolved.search}${resolved.hash}`
  } catch {
    return urlString
  }
}

/** Turn API-relative media paths (`shoots/…`, `/videos/…`) into an absolute URL the browser can load. */
export function toAbsoluteMediaUrl (url: string | null | undefined): string {
  if (url == null || url === '') return ''
  const u = url.trim()
  if (/^https?:\/\//i.test(u)) return alignGunslopMediaToPageOrigin(u)
  if (u.startsWith('//')) {
    const proto = typeof window !== 'undefined' ? window.location.protocol : 'https:'
    return alignGunslopMediaToPageOrigin(`${proto}${u}`)
  }
  const path = u.startsWith('/') ? u : `/${u}`
  const base = resolvePublicSiteBaseUrl().replace(/\/+$/, '')
  return alignGunslopMediaToPageOrigin(`${base}${path}`)
}
